import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Check, Save, Loader2, Eye, EyeOff,
  ChevronLeft, ChevronRight, Download,
} from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../../components/navigation/Sidebar';
import { PatternStudioProvider, usePatternStudio } from './context/PatternStudioContext';
import Step1BasicDetails from './steps/Step1BasicDetails';
import Step2Materials from './steps/Step2Materials';
import Step3Abbreviations from './steps/Step3Abbreviations';
import Step4PatternBuilder from './steps/Step4PatternBuilder';
import Step5Photos from './steps/Step5Photos';
import Step6Branding from './steps/Step6Branding';
import Step7Preview from './steps/Step7Preview';
import Step8Export from './steps/Step8Export';
import PreviewPane from './preview/PreviewPane';

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: 'Details',       shortLabel: '1', component: Step1BasicDetails },
  { id: 2, label: 'Materials',     shortLabel: '2', component: Step2Materials },
  { id: 3, label: 'Abbreviations', shortLabel: '3', component: Step3Abbreviations },
  { id: 4, label: 'Pattern',       shortLabel: '4', component: Step4PatternBuilder },
  { id: 5, label: 'Photos',        shortLabel: '5', component: Step5Photos },
  { id: 6, label: 'Branding',      shortLabel: '6', component: Step6Branding },
  { id: 7, label: 'Preview',       shortLabel: '7', component: Step7Preview },
  { id: 8, label: 'Export',        shortLabel: '8', component: Step8Export },
];

function isStepComplete(stepId, pattern) {
  switch (stepId) {
    case 1: return !!(pattern.title && pattern.animal_type && pattern.difficulty);
    case 2: return pattern.materials?.length > 0;
    case 3: return pattern.abbreviations?.some(a => a.selected);
    case 4: return pattern.sections?.length > 0;
    case 5: return true;
    case 6: return !!(pattern.branding?.shop_name);
    default: return false;
  }
}

// ─── Save status indicator ────────────────────────────────────────────────────
function SaveStatus() {
  const { state } = usePatternStudio();
  if (state.isSaving) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-gray-400">
        <Loader2 size={12} className="animate-spin" /> Saving…
      </span>
    );
  }
  if (state.saveError) {
    return <span className="text-xs text-red-500">Save failed</span>;
  }
  if (state.lastSaved) {
    const t = state.lastSaved;
    const label = t instanceof Date ? t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
    return (
      <span className="flex items-center gap-1 text-xs text-gray-400">
        <Check size={11} className="text-green-500" /> Saved {label}
      </span>
    );
  }
  if (state.isDirty) {
    return <span className="text-xs text-amber-500">Unsaved changes</span>;
  }
  return null;
}

// ─── Step nav item ────────────────────────────────────────────────────────────
function StepNavItem({ step, isActive, isDone, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
        isActive
          ? 'text-gray-900 font-semibold'
          : isDone
          ? 'text-gray-600 hover:bg-stone-50'
          : 'text-gray-400 hover:bg-stone-50'
      }`}
      style={isActive ? { background: 'linear-gradient(135deg, #FAF7F4 0%, #F0EAE0 100%)', border: '1px solid #C4B8A8' } : {}}
    >
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all ${
          isDone ? 'text-white' : isActive ? 'text-white' : 'text-gray-400 bg-gray-100'
        }`}
        style={isDone ? { background: '#6B5744' } : isActive ? { background: '#6B5744' } : {}}
      >
        {isDone ? <Check size={11} /> : step.id}
      </div>
      <span className="text-sm truncate">{step.label}</span>
    </button>
  );
}

// ─── Builder inner (accesses context) ────────────────────────────────────────
function BuilderInner() {
  const navigate = useNavigate();
  const { state, dispatch, save } = usePatternStudio();
  const [showPreview, setShowPreview] = useState(true);

  const ActiveStep = STEPS.find(s => s.id === state.currentStep);
  const StepComponent = ActiveStep?.component;

  function goStep(n) {
    dispatch({ type: 'SET_STEP', step: n });
  }

  async function handleSave() {
    const id = await save(true);
    if (id && !state.patternId) {
      navigate(`/etsy/pattern-studio/${id}/edit`, { replace: true });
    }
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: '#FAF7F4' }}>
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header
        className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 flex-shrink-0 bg-white"
        style={{ zIndex: 10, position: 'relative' }}
      >
        <button
          onClick={() => navigate('/etsy/pattern-studio')}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0"
        >
          <ArrowLeft size={16} />
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="text-sm font-semibold text-gray-900 truncate leading-tight">
            {state.pattern.title || 'Untitled Pattern'}
          </h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-stone-400 shrink-0">
              {state.currentStep} / {STEPS.length} · {STEPS.find(s => s.id === state.currentStep)?.label}
            </span>
            <SaveStatus />
          </div>
        </div>

        <button
          onClick={() => setShowPreview(v => !v)}
          className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            showPreview ? 'bg-stone-100 text-gray-700' : 'bg-white border border-gray-200 text-gray-500'
          }`}
        >
          {showPreview ? <EyeOff size={13} /> : <Eye size={13} />}
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>

        <button
          onClick={handleSave}
          disabled={state.isSaving || !state.isDirty}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-40 transition-all active:scale-95"
          style={{ background: 'linear-gradient(135deg, #6B5744 0%, #8B7355 100%)' }}
        >
          {state.isSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save
        </button>

        <button
          onClick={() => goStep(8)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border border-stone-300 text-gray-700 hover:bg-stone-50 transition-all"
        >
          <Download size={13} />
          Export
        </button>

        {/* Step progress bar */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: '#F0EAE0' }}>
          <div
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #6B5744 0%, #8B7355 100%)',
              width: `${(state.currentStep / STEPS.length) * 100}%`,
              transition: 'width 0.3s ease',
              borderRadius: '0 2px 2px 0',
            }}
          />
        </div>
      </header>

      {/* ── Body: step nav + edit panel + preview ───────────────────────── */}
      <div className="flex flex-1 min-h-0">
        {/* Step nav (left) */}
        <nav
          className="hidden md:flex flex-col gap-0.5 p-3 border-r border-stone-200 bg-white flex-shrink-0"
          style={{ width: '168px' }}
        >
          {STEPS.slice(0, 6).map(step => (
            <StepNavItem
              key={step.id}
              step={step}
              isActive={state.currentStep === step.id}
              isDone={isStepComplete(step.id, state.pattern)}
              onClick={() => goStep(step.id)}
            />
          ))}
          <div className="my-2 border-t border-stone-100" />
          {STEPS.slice(6).map(step => (
            <StepNavItem
              key={step.id}
              step={step}
              isActive={state.currentStep === step.id}
              isDone={false}
              onClick={() => goStep(step.id)}
            />
          ))}
        </nav>

        {/* Edit panel (center) */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="flex-1 overflow-y-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={state.currentStep}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.18 }}
                className="h-full"
              >
                {StepComponent && <StepComponent />}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Prev/Next footer */}
          {state.currentStep < 8 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-stone-200 bg-white flex-shrink-0">
              <button
                onClick={() => goStep(Math.max(1, state.currentStep - 1))}
                disabled={state.currentStep === 1}
                className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 transition-all"
              >
                <ChevronLeft size={14} /> Back
              </button>

              {/* Mobile step dots */}
              <div className="flex gap-1.5 md:hidden">
                {STEPS.map(s => (
                  <button
                    key={s.id}
                    onClick={() => goStep(s.id)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      s.id === state.currentStep ? 'w-5' : 'bg-stone-200'
                    }`}
                    style={s.id === state.currentStep ? { background: '#6B5744' } : {}}
                  />
                ))}
              </div>

              <button
                onClick={() => goStep(Math.min(8, state.currentStep + 1))}
                className="flex items-center gap-1 px-4 py-2 text-sm text-white rounded-xl transition-all active:scale-95"
                style={{ background: 'linear-gradient(135deg, #6B5744 0%, #8B7355 100%)' }}
              >
                {state.currentStep === 7 ? 'Go to Export' : 'Next'} <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Live preview (right) */}
        <AnimatePresence>
          {showPreview && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 380, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="hidden lg:block flex-shrink-0 border-l border-stone-200 bg-white overflow-hidden"
            >
              <PreviewPane />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─── Exported page (wraps context provider) ───────────────────────────────────
export default function PatternStudioBuilder() {
  const { id } = useParams();

  return (
    <SidebarProvider>
      <PatternStudioProvider patternId={id}>
        <BuilderInner />
      </PatternStudioProvider>
    </SidebarProvider>
  );
}
